import { Offcanvas } from 'react-bootstrap'
import { FormInput } from '../../../components/forms/input/FormInput'
import { FormSelect } from '../../../components/forms/select/FormSelect'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { actualizarFiltros, establecerPagina } from '../../../redux/slices/pagina'
import { useRecuperarOpcCatalogoQuery } from '../../../redux/api'
import { AppCodigosCatalogo } from '../../../utils/Constantes'
import { generarArregloFiltrosAplicados } from '../../../utils/FiltrosUtils'
import { BaseSyntheticEvent, useEffect, useState } from 'react'

type Props = {
  mostrarOffcanva: boolean
  cerrarOffCanva: () => void
}

type TFormInputs = {
  nombreCompleto: string | any
  divisionNegocio: string | any
  perfil: string | any
  posicion: string | any
}

const filtrosBusqueda = {
  nombreCompleto: { etiqueta: 'Nombre', tipo: 'text' },
  divisionNegocio: { etiqueta: 'División de Negocio', tipo: 'select' },
  perfil: { etiqueta: 'Perfil', tipo: 'select' },
  posicion: { etiqueta: 'Posición', tipo: 'select' },
}

/**
 * Offcanvas de filtros para el listado de usuarios.
 * Mismo patron que OffcanvasFiltrosSitios: slice `pagina` global,
 * hidratacion desde filtros existentes, normalizacion select→valor.
 */
export const OffcanvasFiltrosUsuarios = ({ cerrarOffCanva, mostrarOffcanva }: Props) => {
  const dispatch = useDispatch()
  const filtrosPagina = useSelector((state: RootState) => state.pagina)
  const { register, handleSubmit, setValue, control, reset } = useForm<TFormInputs>({
    mode: 'all',
    defaultValues: {},
  })

  const [envioFormEnter, setEnvioFormEnter] = useState(false)

  const enviarFormEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setEnvioFormEnter(true)
      handleSubmit(onSubmit)

      cerrarOffCanva()
    }
    if (!mostrarOffcanva && event.key !== 'Enter') {
      setEnvioFormEnter(false)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', enviarFormEnter)
    return () => {
      document.removeEventListener('keydown', enviarFormEnter)
    }
  }, [envioFormEnter])

  const { data: catPosicionesContacto } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.POSICION_CONTACTO
  )
  const { data: catDivisionesNegocio } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.DIVISION_NEGOCIO
  )
  const { data: catPerfiles } = useRecuperarOpcCatalogoQuery(AppCodigosCatalogo.PERFILES_USUARIOS)

  /**
   * @description Llena los inputs del offcanva a partir de si hay filtros seleccionados al entrar al offcanva
   * @author luis.durazo, Hábil MX
   */
  const handleOnEntering = () => {
    reset()
    setValue('nombreCompleto', null)
    setValue('divisionNegocio', null)
    setValue('perfil', null)
    setValue('posicion', null)
    filtrosPagina.filtros.forEach((filtro: any) => {
      if (filtrosBusqueda[filtro.clave as keyof TFormInputs].tipo === 'select') {
        setValue(filtro.clave as any, { valor: filtro.valor, etiqueta: filtro.descripcion })
      } else {
        setValue(filtro.clave as any, filtro.valor)
      }
    })
  }

  /**
   * @description Actualiza y aplica filtros de busqueda de usuarios
   * @author andres.martinez, Hábil MX
   * @param data Valores del formulario
   */
  const onSubmit: SubmitHandler<TFormInputs> = (data: TFormInputs, TFormInputs) => {
    const filtros = generarArregloFiltrosAplicados(data, filtrosBusqueda, TFormInputs)
    dispatch(actualizarFiltros(filtros))
    cerrarOffCanva()
    dispatch(establecerPagina(1))
  }

  return (
    <>
      <Offcanvas
        show={mostrarOffcanva}
        onHide={cerrarOffCanva}
        scroll={true}
        backdrop={true}
        placement='end'
        onEntering={() => {
          handleOnEntering()
          setEnvioFormEnter(false)
        }}
        onExit={envioFormEnter && handleSubmit(onSubmit)}>
        <Offcanvas.Header closeButton className='offcanvas-header-separator'>
          <div className='d-flex flex-column'>
            <h2>Filtros</h2>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='row'>
              <div className='col-12 mb-5'>
                <Controller
                  control={control}
                  name='nombreCompleto'
                  render={({ field: { onChange } }) => (
                    <FormInput
                      className='mb-5'
                      nombre='Nombre'
                      nombreCampo='nombreCompleto'
                      register={register}
                      placeholder='Ej. Ricardo'
                      maxLength={50}
                      onChange={(e: BaseSyntheticEvent) => {
                        onChange(e)
                      }}
                    />
                  )}
                />
              </div>
              <div className='col-12 mb-10'>
                <Controller
                  control={control}
                  name='divisionNegocio'
                  render={({ field: { onChange, value } }) => (
                    <FormSelect
                      nombre='División de negocio'
                      nombreCampo='divisionNegocio'
                      value={value}
                      elementos={catDivisionesNegocio}
                      onChange={(e: Event) => {
                        onChange(e)
                      }}
                      placeholder='Ej. Industrial'
                    />
                  )}
                />
              </div>
              <div className='col-12 mb-10'>
                <Controller
                  control={control}
                  name='posicion'
                  render={({ field: { onChange, value } }) => (
                    <FormSelect
                      nombre='Posición'
                      nombreCampo='posicion'
                      value={value}
                      elementos={catPosicionesContacto}
                      onChange={(e: Event) => {
                        onChange(e)
                      }}
                      placeholder='Ej. Jefe de calidad'
                    />
                  )}
                />
              </div>
              <div className='col-12 mb-10'>
                <Controller
                  control={control}
                  name='perfil'
                  render={({ field: { onChange, value } }) => (
                    <FormSelect
                      nombre='Perfil'
                      nombreCampo='perfil'
                      value={value}
                      elementos={catPerfiles}
                      onChange={(e: Event) => {
                        onChange(e)
                      }}
                      placeholder='Ej. Coordinador'
                    />
                  )}
                />
              </div>
            </div>
            <div className='d-flex justify-content-end mt-7'>
              <button className='btn me-2' type='button' onClick={cerrarOffCanva}>
                Cancelar
              </button>
              <button className='btn btn-primary' type='submit'>
                Buscar <i className='fa-solid fa-arrow-right-long ps-2'></i>
              </button>
            </div>
          </form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  )
}
